import { LoaderCircle } from 'lucide-preact';
import { ComponentChildren } from 'preact';

import { Button } from '@/components/Buttons';
import { ProductFeaturesList, ProductFeaturesListItem } from '@/components/products/ProductFeatureList';

export interface PromotedProductSectionProps {
  /** Defaults to "You Might Like" */
  headline?: string;
  image: ComponentChildren;
  title: string;
  subtitle: string;
  priceRow: ComponentChildren;
  benefitLines: string[];
  primaryButton: {
    label: string;
    variant?: 'default' | 'outline';
    disabled?: boolean;
    loading?: boolean;
    onClick: () => void;
    dataId?: string;
  };
  helperContent?: ComponentChildren;
}

/** Presentational "You Might Like" block shared by recurring tier changes and checkout upsells */
const PromotedProductSection = ({
  headline = 'You Might Like',
  image,
  title,
  subtitle,
  priceRow,
  benefitLines,
  primaryButton,
  helperContent,
}: PromotedProductSectionProps) => {
  const { label, variant = 'default', disabled, loading, onClick, dataId = 'promoted-product-button' } = primaryButton;

  return (
    <div className="px-3 py-5 desktop:px-6">
      <p className="preflight preflight-p mb-8 text-lg font-medium text-stone-500 desktop:text-xl">{headline}</p>
      <div className="flex min-w-0 items-center gap-3 mobile:w-full desktop:grow">
        {image}
        <div>
          <h6 className="preflight preflight-h6 line-clamp-1 text-lg font-bold text-neutral-600 desktop:text-2xl">
            {title}
          </h6>
          <p className="preflight preflight-p line-clamp-2 text-base text-stone-500 desktop:text-xl">{subtitle}</p>
        </div>
      </div>
      <div className="my-5 flex flex-wrap items-center gap-4">{priceRow}</div>
      <ProductFeaturesList className="w-full">
        {benefitLines.map((benefit, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <ProductFeaturesListItem key={index.toString()}>{benefit}</ProductFeaturesListItem>
        ))}
      </ProductFeaturesList>
      <div className="mt-10 flex w-full items-center justify-center gap-4 mobile:flex-col desktop:justify-between desktop:gap-[60px]">
        <Button
          data-id={dataId}
          variant={variant}
          disabled={disabled || loading}
          size="lg"
          className="min-w-40 shrink-0 px-5 py-3 text-base/none mobile:w-full desktop:order-2"
          onClick={onClick}
        >
          {loading ? <LoaderCircle className="size-4 animate-spin" /> : label}
        </Button>
        <div className="preflight text-base font-normal text-stone-500 desktop:text-xl">{helperContent}</div>
      </div>
    </div>
  );
};

export default PromotedProductSection;
